import { useState, useCallback } from "react";
import { validateField } from "../../utils/validation.js";
import ErrorMessage from "../common/ErrorMessage.jsx";

const FormField = ({
  label,
  name,
  type = "text",
  value = "",
  onChange,
  onBlur,
  placeholder = "",
  required = false,
  disabled = false,
  className = "",
  error = null,
  formData = {},
  validateOnChange = false,
  validateOnBlur = true,
  autoComplete = "off",
  maxLength,
  options = [],
  ...props
}) => {
  const [localError, setLocalError] = useState(null);
  const [touched, setTouched] = useState(false);

  // Use provided error or local validation error
  const displayError = error || (touched ? localError : null);

  const handleChange = useCallback(
    (e) => {
      const newValue = e.target.value;

      // Call parent onChange
      if (onChange) {
        onChange(e);
      }

      // Validate on change if enabled
      if (validateOnChange && touched) {
        const validationError = validateField(name, newValue, formData);
        setLocalError(validationError);
      }
    },
    [onChange, validateOnChange, touched, name, formData],
  );

  const handleBlur = useCallback(
    (e) => {
      setTouched(true);

      // Call parent onBlur
      if (onBlur) {
        onBlur(e);
      }

      // Validate on blur if enabled
      if (validateOnBlur) {
        const validationError = validateField(name, e.target.value, formData);
        setLocalError(validationError);
      }
    },
    [onBlur, validateOnBlur, name, formData],
  );

  const fieldId = `field-${name}`;
  const hasError = Boolean(displayError);

  return (
    <div className={`form-field ${className} ${hasError ? "has-error" : ""}`}>
      {label && (
        <label htmlFor={fieldId} className="form-label">
          {label}
          {required && (
            <span className="required-indicator" aria-label="required">
              *
            </span>
          )}
        </label>
      )}

      <div className="form-input-container">
        {type === "textarea" ? (
          <textarea
            id={fieldId}
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            className={`form-textarea ${hasError ? "error" : ""}`}
            autoComplete={autoComplete}
            maxLength={maxLength}
            {...props}
          />
        ) : type === "select" ? (
          <select
            id={fieldId}
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            required={required}
            disabled={disabled}
            className={`form-select ${hasError ? "error" : ""}`}
            autoComplete={autoComplete}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => {
              // Handle both string options and object options {value, label}
              const optionValue =
                typeof option === "object" ? option.value : option;
              const optionLabel =
                typeof option === "object" ? option.label : option;
              return (
                <option key={optionValue} value={optionValue}>
                  {optionLabel}
                </option>
              );
            })}
          </select>
        ) : (
          <input
            id={fieldId}
            name={name}
            type={type}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            className={`form-input ${hasError ? "error" : ""}`}
            autoComplete={autoComplete}
            maxLength={maxLength}
            {...props}
          />
        )}
      </div>

      <ErrorMessage message={displayError} className="field-error" />
    </div>
  );
};

export default FormField;
